import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Eye, ShoppingCart, Star, Store, Package, Info, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getProductImage } from '@/utils/imageUtils';
import { safeGetJSON } from '@/utils/safeStorage';
import CreatorOfTheWeekCard from '@/components/CreatorOfTheWeekCard';
import { VerifiedSellerBadge } from '@/components/VerifiedSellerBadge';

function MarketplacePreview() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState(null);
  const user = safeGetJSON('user', null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load all marketplace products
      const productsRes = await axios.get('/products');
      setProducts(productsRes.data.products || []);

      // Load creator's own products
      if (user) {
        const creatorRes = await axios.get('/creators/me');
        setCreator(creatorRes.data);
        
        const myProductsRes = await axios.get('/creators/me/products');
        setMyProducts(myProductsRes.data.products || []);
      }
    } catch (error) {
      console.error('Error loading preview data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Combine marketplace products with creator's products (highlighted)
  const allProducts = [...products];
  const myProductIds = myProducts.map(p => p.product_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-[#00F0FF] to-white text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-semibold">Marketplace Preview Mode</span>
            <span className="text-sm opacity-80">- This is how buyers will see the marketplace</span>
          </div>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="sm"
            className="border-white/30 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Info Banner for Creator */}
      <div className="bg-[#00F0FF]/10 border-b border-[#00F0FF]/30 py-4 px-4">
        <div className="max-w-7xl mx-auto flex items-start gap-3">
          <Info className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#00F0FF] font-semibold">Your Products Are Highlighted</p>
            <p className="text-sm text-neutral-300">
              Products with a golden border are yours. You have {myProducts.length} product{myProducts.length !== 1 ? 's' : ''} listed.
              {myProducts.length === 0 && " Add your first product to see it appear here!"}
            </p>
          </div>
        </div>
      </div>

      {/* Mock Header */}
      <header className="border-b border-white/10 py-4 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00F0FF] to-white rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">AI</span>
            </div>
            <span className="text-xl font-bold">theailedger.ai</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-6 text-sm text-neutral-400">
              <span className="hover:text-white cursor-pointer">Categories</span>
              <span className="hover:text-white cursor-pointer">Creators</span>
              <span className="hover:text-white cursor-pointer">Deals</span>
            </div>
            <Button variant="outline" size="sm" className="border-white/20">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Cart
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 text-center bg-gradient-to-b from-white/10 to-transparent">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-black mb-4">
            The Marketplace for{' '}
            <span className="bg-gradient-to-r from-[#00F0FF] via-white to-[#00F0FF] bg-clip-text text-transparent">
              AI Products
            </span>
          </h1>
          <p className="text-lg text-neutral-400 mb-8">
            Discover, buy, and sell cutting-edge AI tools and products from entrepreneurs worldwide.
          </p>
          <div className="flex justify-center gap-4">
            <Button className="bg-white text-black hover:bg-[#00C2CC] hover:text-black rounded-full px-8">
              Browse Products
            </Button>
            <Button variant="outline" className="border-white/20 rounded-full px-8">
              Start Selling
            </Button>
          </div>
        </div>
      </section>

      {/* Creator of the Week (fetches itself; auto-hides if none) */}
      <CreatorOfTheWeekCard className="!border-t-0" />

      {/* Products Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold">Featured Products</h2>
            <span className="text-sm text-neutral-400">{allProducts.length} products</span>
          </div>

          {allProducts.length === 0 ? (
            <div className="text-center py-16 bg-neutral-900/50 rounded-2xl border border-white/10">
              <Package className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Products Yet</h3>
              <p className="text-neutral-400 mb-6">Be the first to list a product on the marketplace!</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full"
              >
                Add Your First Product
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allProducts.map((product) => {
                const isMyProduct = myProductIds.includes(product.product_id);
                return (
                  <div
                    key={product.product_id}
                    className={`bg-neutral-900/50 rounded-2xl overflow-hidden border transition-all hover:scale-[1.02] ${
                      isMyProduct 
                        ? 'border-[#00F0FF] ring-2 ring-[#00F0FF]/30' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    {isMyProduct && (
                      <div className="bg-[#00F0FF] text-black text-xs font-bold px-3 py-1 text-center">
                        ⭐ YOUR PRODUCT
                      </div>
                    )}
                    <div className="aspect-video bg-neutral-800 relative">
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=400&fit=crop&auto=format'; }}
                        />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{product.name}</h3>
                      <p className="text-sm text-neutral-400 mb-3 line-clamp-2">
                        {product.description || 'No description'}
                      </p>
                      {product.is_verified_seller && (
                        <div className="mb-3">
                          <VerifiedSellerBadge verified={true} size="sm" />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-[#00F0FF]">
                          ${product.price?.toFixed(2) || '0.00'}
                        </span>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm">
                          <Star className="w-4 h-4 fill-current" />
                          <span>{product.rating?.toFixed(1) || '5.0'}</span>
                        </div>
                      </div>
                      <Button 
                        className="w-full mt-4 bg-white text-black hover:bg-[#00C2CC] hover:text-black rounded-xl"
                        disabled
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Your Store Section */}
      {creator && (
        <section className="py-12 px-4 bg-gradient-to-b from-transparent to-[#00F0FF]/5">
          <div className="max-w-7xl mx-auto">
            <div className="bg-neutral-900/50 border border-[#00F0FF]/30 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                  <Store className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{creator.store_name || 'Your Store'}</h3>
                  <p className="text-neutral-400 text-sm">This is how your store appears to buyers</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Button
                  onClick={() => window.open(`/store/${creator.store_slug}`, '_blank')}
                  className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-xl"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Your Store Page
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="border-white/20 rounded-xl"
                >
                  Manage Products
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 text-center text-neutral-500 text-sm">
        <p>© 2026 theailedger.ai - Preview Mode</p>
        <p className="mt-2">This is a preview of how buyers will see the marketplace when it launches.</p>
      </footer>
    </div>
  );
}

export default MarketplacePreview;
